import { useState, useEffect, useCallback, useRef } from 'react'

export type PermissionStatus = 'prompt' | 'granted' | 'denied' | 'unavailable'

// Local storage keys for device preferences
const STORAGE_KEYS = {
  audioInput: 'meeting-audio-input-device',
  audioOutput: 'meeting-audio-output-device',
  videoInput: 'meeting-video-input-device',
}

// Load saved device preference from localStorage
const loadSavedDevice = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

// Save device preference to localStorage
const saveDevice = (key: string, deviceId: string) => {
  try {
    localStorage.setItem(key, deviceId)
  } catch {
    // Ignore storage errors
  }
}

interface UseMediaDevicesReturn {
  // Available devices
  audioInputDevices: MediaDeviceInfo[]
  audioOutputDevices: MediaDeviceInfo[]
  videoInputDevices: MediaDeviceInfo[]

  // Selected devices
  selectedAudioInputId: string | null
  selectedAudioOutputId: string | null
  selectedVideoInputId: string | null

  // Actions
  selectAudioInput: (deviceId: string) => void
  selectAudioOutput: (deviceId: string) => void
  selectVideoInput: (deviceId: string) => void
  requestPermissions: () => Promise<boolean>
  refreshDevices: () => Promise<void>
  stopStreams: () => void

  // State
  audioPermission: PermissionStatus
  videoPermission: PermissionStatus
  isLoading: boolean
  error: string | null

  // Active streams (when permission granted)
  audioStream: MediaStream | null
  videoStream: MediaStream | null
}

export function useMediaDevices(): UseMediaDevicesReturn {
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([])
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([])
  const [videoInputDevices, setVideoInputDevices] = useState<MediaDeviceInfo[]>([])

  // Initialize with saved preferences from localStorage
  const [selectedAudioInputId, setSelectedAudioInputId] = useState<string | null>(
    () => loadSavedDevice(STORAGE_KEYS.audioInput)
  )
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState<string | null>(
    () => loadSavedDevice(STORAGE_KEYS.audioOutput)
  )
  const [selectedVideoInputId, setSelectedVideoInputId] = useState<string | null>(
    () => loadSavedDevice(STORAGE_KEYS.videoInput)
  )

  const [audioPermission, setAudioPermission] = useState<PermissionStatus>('prompt')
  const [videoPermission, setVideoPermission] = useState<PermissionStatus>('prompt')

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [audioStream, setAudioStreamState] = useState<MediaStream | null>(null)
  const [videoStream, setVideoStreamState] = useState<MediaStream | null>(null)

  // Refs to track current streams for cleanup (avoid stale closures)
  const audioStreamRef = useRef<MediaStream | null>(null)
  const videoStreamRef = useRef<MediaStream | null>(null)

  // Wrapper to update both state and ref
  const setAudioStream = useCallback((stream: MediaStream | null) => {
    audioStreamRef.current = stream
    setAudioStreamState(stream)
  }, [])

  const setVideoStream = useCallback((stream: MediaStream | null) => {
    videoStreamRef.current = stream
    setVideoStreamState(stream)
  }, [])

  // Stop all active streams (for cleanup)
  const stopStreams = useCallback(() => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop())
      audioStreamRef.current = null
      setAudioStreamState(null)
    }
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach(track => track.stop())
      videoStreamRef.current = null
      setVideoStreamState(null)
    }
  }, [])

  // Enumerate available devices
  const refreshDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()

      const audioInputs = devices.filter(d => d.kind === 'audioinput')
      const audioOutputs = devices.filter(d => d.kind === 'audiooutput')
      const videoInputs = devices.filter(d => d.kind === 'videoinput')

      setAudioInputDevices(audioInputs)
      setAudioOutputDevices(audioOutputs)
      setVideoInputDevices(videoInputs)

      // Check if saved device is still available, otherwise fall back to default
      const savedAudioInput = loadSavedDevice(STORAGE_KEYS.audioInput)
      const savedAudioOutput = loadSavedDevice(STORAGE_KEYS.audioOutput)
      const savedVideoInput = loadSavedDevice(STORAGE_KEYS.videoInput)

      // Validate saved audio input device
      if (savedAudioInput && audioInputs.some(d => d.deviceId === savedAudioInput)) {
        setSelectedAudioInputId(savedAudioInput)
      } else if (!selectedAudioInputId && audioInputs.length > 0) {
        const defaultInput = audioInputs.find(d => d.deviceId === 'default') || audioInputs[0]
        setSelectedAudioInputId(defaultInput.deviceId)
        saveDevice(STORAGE_KEYS.audioInput, defaultInput.deviceId)
      }

      // Validate saved audio output device
      if (savedAudioOutput && audioOutputs.some(d => d.deviceId === savedAudioOutput)) {
        setSelectedAudioOutputId(savedAudioOutput)
      } else if (!selectedAudioOutputId && audioOutputs.length > 0) {
        const defaultOutput = audioOutputs.find(d => d.deviceId === 'default') || audioOutputs[0]
        setSelectedAudioOutputId(defaultOutput.deviceId)
        saveDevice(STORAGE_KEYS.audioOutput, defaultOutput.deviceId)
      }

      // Validate saved video input device
      if (savedVideoInput && videoInputs.some(d => d.deviceId === savedVideoInput)) {
        setSelectedVideoInputId(savedVideoInput)
      } else if (!selectedVideoInputId && videoInputs.length > 0) {
        setSelectedVideoInputId(videoInputs[0].deviceId)
        saveDevice(STORAGE_KEYS.videoInput, videoInputs[0].deviceId)
      }
    } catch (err) {
      console.error('Failed to enumerate devices:', err)
      setError('Failed to list media devices')
    }
  }, [selectedAudioInputId, selectedAudioOutputId, selectedVideoInputId])

  // Check permission status
  const checkPermissions = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setAudioPermission('unavailable')
      setVideoPermission('unavailable')
      setError('Media devices not supported in this browser')
      return
    }

    try {
      if (navigator.permissions && navigator.permissions.query) {
        // Check microphone permission
        try {
          const micResult = await navigator.permissions.query({ name: 'microphone' as PermissionName })
          setAudioPermission(micResult.state as PermissionStatus)
          micResult.addEventListener('change', () => {
            setAudioPermission(micResult.state as PermissionStatus)
            if (micResult.state === 'granted') refreshDevices()
          })
        } catch {
          // Some browsers don't support this query
        }

        // Check camera permission
        try {
          const camResult = await navigator.permissions.query({ name: 'camera' as PermissionName })
          setVideoPermission(camResult.state as PermissionStatus)
          camResult.addEventListener('change', () => {
            setVideoPermission(camResult.state as PermissionStatus)
            if (camResult.state === 'granted') refreshDevices()
          })
        } catch {
          // Some browsers don't support this query
        }
      }
    } catch {
      // Permission query not supported
    }
  }, [refreshDevices])

  // Request audio and video permissions
  const requestPermissions = useCallback(async (): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      // Stop existing streams if any
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop())
      }
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop())
      }

      // Request audio stream with processing constraints
      const audioConstraints: MediaStreamConstraints = {
        audio: {
          ...(selectedAudioInputId ? { deviceId: { exact: selectedAudioInputId } } : {}),
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      }

      const newAudioStream = await navigator.mediaDevices.getUserMedia(audioConstraints)
      setAudioStream(newAudioStream)
      setAudioPermission('granted')

      // Request video stream
      const videoConstraints: MediaStreamConstraints = {
        audio: false,
        video: selectedVideoInputId
          ? { deviceId: { exact: selectedVideoInputId } }
          : {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              facingMode: 'user',
            },
      }

      const newVideoStream = await navigator.mediaDevices.getUserMedia(videoConstraints)
      setVideoStream(newVideoStream)
      setVideoPermission('granted')

      // Refresh device list now that we have permissions
      await refreshDevices()

      setIsLoading(false)
      return true
    } catch (err) {
      console.error('Failed to get media streams:', err)

      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError('Camera/microphone access denied. Please enable in browser settings.')
        } else if (err.name === 'NotFoundError') {
          setError('No camera or microphone found. Please connect devices.')
        } else if (err.name === 'NotReadableError') {
          setError('Camera or microphone is in use by another application.')
        } else {
          setError(`Failed to access media devices: ${err.message}`)
        }
      } else {
        setError('Unknown error accessing media devices')
      }

      setIsLoading(false)
      return false
    }
  }, [selectedAudioInputId, selectedVideoInputId, audioStream, videoStream, refreshDevices])

  // Select audio input device
  const selectAudioInput = useCallback(async (deviceId: string) => {
    setSelectedAudioInputId(deviceId)
    saveDevice(STORAGE_KEYS.audioInput, deviceId) // Persist to localStorage

    if (audioPermission === 'granted' && audioStream) {
      try {
        audioStream.getTracks().forEach(track => track.stop())
        const newStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: deviceId },
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        })
        setAudioStream(newStream)
      } catch (err) {
        console.error('Failed to switch audio device:', err)
        setError('Failed to switch microphone')
      }
    }
  }, [audioPermission, audioStream])

  // Select audio output device
  const selectAudioOutput = useCallback((deviceId: string) => {
    setSelectedAudioOutputId(deviceId)
    saveDevice(STORAGE_KEYS.audioOutput, deviceId) // Persist to localStorage
  }, [])

  // Select video input device
  const selectVideoInput = useCallback(async (deviceId: string) => {
    setSelectedVideoInputId(deviceId)
    saveDevice(STORAGE_KEYS.videoInput, deviceId) // Persist to localStorage

    if (videoPermission === 'granted' && videoStream) {
      try {
        videoStream.getTracks().forEach(track => track.stop())
        const newStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { deviceId: { exact: deviceId } },
        })
        setVideoStream(newStream)
      } catch (err) {
        console.error('Failed to switch video device:', err)
        setError('Failed to switch camera')
      }
    }
  }, [videoPermission, videoStream])

  // Initialize
  useEffect(() => {
    const init = async () => {
      setIsLoading(true)
      await checkPermissions()
      await refreshDevices()
      setIsLoading(false)
    }

    init()

    // Listen for device changes
    const handleDeviceChange = () => {
      refreshDevices()
    }

    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange)

    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange)
      // Cleanup streams on unmount using refs (not stale state)
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop())
      }
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  return {
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
    selectedAudioInputId,
    selectedAudioOutputId,
    selectedVideoInputId,
    selectAudioInput,
    selectAudioOutput,
    selectVideoInput,
    requestPermissions,
    refreshDevices,
    stopStreams,
    audioPermission,
    videoPermission,
    isLoading,
    error,
    audioStream,
    videoStream,
  }
}
